import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

const STT_PROVIDER_OPTIONS = [
  { key: "deepgram:nova-3", label: "Deepgram Nova 3" },
  { key: "elevenlabs:scribe-v2", label: "ElevenLabs Scribe v2" },
  { key: "openai:whisper-1", label: "OpenAI Whisper 1" },
] as const

type STTProviderModel = (typeof STT_PROVIDER_OPTIONS)[number]["key"]

const DEFAULT_STT_PROVIDER_MODEL: STTProviderModel = "deepgram:nova-3"

interface TranscriptionErrorConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

function parseUnitFloat(raw: string): number | undefined {
  if (raw.trim() === "") {
    return undefined
  }
  const value = parseFloat(raw)
  if (Number.isNaN(value)) {
    return undefined
  }
  return Math.min(Math.max(value, 0), 1)
}

export const TranscriptionErrorConfig: React.FC<
  TranscriptionErrorConfigProps
> = ({ metricRecordData, onChange }) => {
  const sttProviderModel =
    metricRecordData.metric_metadata?.stt_provider_model ??
    DEFAULT_STT_PROVIDER_MODEL
  const minReferenceConfidence =
    metricRecordData.metric_metadata?.min_reference_confidence
  const minSubstituteSimilarity =
    metricRecordData.metric_metadata?.min_substitute_similarity

  const handleSttProviderChange = (nextProvider: STTProviderModel) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        stt_provider_model: nextProvider,
      },
    })
  }

  const handleMinReferenceConfidenceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_reference_confidence: parseUnitFloat(e.target.value),
      },
    })
  }

  const handleMinSubstituteSimilarityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_substitute_similarity: parseUnitFloat(e.target.value),
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>STT provider</label>
        </div>
        <Select
          value={sttProviderModel}
          onValueChange={(value) =>
            handleSttProviderChange(value as STTProviderModel)
          }
        >
          <SelectTrigger aria-label="Select STT provider and model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STT_PROVIDER_OPTIONS.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          STT engine producing the reference transcription. Whisper does not
          support mono audio.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Min reference confidence</label>
        </div>
        <input
          type="number"
          step="0.01"
          min="0"
          max="1"
          className={TEXT_INPUT_STYLES}
          value={minReferenceConfidence ?? ""}
          onChange={handleMinReferenceConfidenceChange}
          placeholder="off"
          aria-label="Minimum reference word confidence"
        />
        <p className="text-xs text-coval-text-muted">
          Drop delete/replace errors where the reference word&apos;s recognizer
          confidence is below this threshold (0&ndash;1). Leave blank to keep
          all errors.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Min substitute similarity</label>
        </div>
        <input
          type="number"
          step="0.01"
          min="0"
          max="1"
          className={TEXT_INPUT_STYLES}
          value={minSubstituteSimilarity ?? ""}
          onChange={handleMinSubstituteSimilarityChange}
          placeholder="off"
          aria-label="Minimum substitute similarity"
        />
        <p className="text-xs text-coval-text-muted">
          Drop replace errors where the reference and hypothesis words are at
          least this similar (Levenshtein, 0&ndash;1). Useful for ignoring
          variants like &ldquo;color&rdquo;/&ldquo;colour&rdquo;. Leave blank to
          keep all errors.
        </p>
      </div>
    </div>
  )
}
